/**
 * Session lifecycle + the authenticated fetch wrapper every client call goes
 * through (techsheet rules 4 + 5).
 *
 * **Two auth transports, one code path:**
 * - Web → Payload's httpOnly cookie. Nothing is stored client-side; requests
 *   carry `credentials: 'include'` and the browser does the rest.
 * - Capacitor → the JWT from the login response, kept in Secure Storage and sent
 *   as `Authorization: JWT <token>`. The WebView origin (`capacitor://localhost`)
 *   is not the API's origin, so cookies are not an option there.
 *
 * `apiFetchAuth` hides that difference: callers just pass a path.
 *
 * The beta `@payloadcms/sdk` is deliberately NOT used — this wrapper is the
 * whole client, and it is the piece that owns the 401 retry.
 */
import { z } from 'zod'

import { apiUrl } from './config'
import { isNative } from './platform'
import { kv } from './storage'

const SESSION_KEY = 'bitez_session_v1'

/** Ids are UUIDs (`idType: 'uuid'` in payload.config.ts) — strings everywhere. */
const sessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
})

/** `POST /api/users/login` and `POST /api/users/refresh-token` share this shape. */
const authResponseSchema = z.object({
  user: sessionUserSchema,
  token: z.string(),
  /** Seconds since epoch (JWT standard) — normalised to ms when stored. */
  exp: z.number(),
})

const meResponseSchema = z.object({
  user: sessionUserSchema.nullable(),
})

export type SessionUser = z.infer<typeof sessionUserSchema>

export type Session = {
  user: SessionUser
  /** Native only — on the web the cookie is the token and it never reaches JS. */
  token?: string
  /** Unix epoch ms. Native only. */
  expMs?: number
}

let memCache: Session | null = null
let hydrated = false

const listeners = new Set<(session: Session | null) => void>()

/** Subscribe to session changes (login / logout / token loss). Returns unsubscribe. */
export function subscribeSession(callback: (session: Session | null) => void): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

function notify(session: Session | null): void {
  for (const listener of listeners) listener(session)
}

function hydrateFromStorage(): void {
  if (hydrated) return
  hydrated = true
  // Web keeps no session copy — the cookie is the source of truth and
  // `/api/users/me` is what resolves it.
  if (!isNative()) return
  try {
    const raw = kv.getSync(SESSION_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Session
    if (parsed.expMs && parsed.expMs < Date.now()) {
      kv.remove(SESSION_KEY)
      return
    }
    memCache = parsed
  } catch {
    kv.remove(SESSION_KEY)
  }
}

function persist(session: Session | null): void {
  if (!isNative()) return
  if (session) kv.set(SESSION_KEY, JSON.stringify(session))
  else kv.remove(SESSION_KEY)
}

export function setSession(session: Session): void {
  hydrateFromStorage()
  memCache = session
  persist(session)
  notify(session)
}

export function clearSession(): void {
  hydrateFromStorage()
  memCache = null
  persist(null)
  notify(null)
}

/** Snapshot, no side effects. Native: the stored session. Web: whatever `fetchMe` last resolved. */
export function getSession(): Session | null {
  hydrateFromStorage()
  return memCache
}

// ─── Requests ────────────────────────────────────────────────────────────

function buildInit(init: RequestInit | undefined, token: string | undefined): RequestInit {
  const headers = new Headers(init?.headers)
  if (token) headers.set('Authorization', `JWT ${token}`)
  if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  return {
    ...init,
    headers,
    // Web auth is cookie-based; on native the header carries it and the cookie is ignored.
    credentials: 'include',
  }
}

/**
 * Refresh the JWT once. Payload extends the cookie on the web and hands back a
 * fresh token for native. Returns false when the session is truly gone.
 */
async function refreshSession(): Promise<boolean> {
  const current = getSession()
  try {
    const res = await fetch(
      apiUrl('/api/users/refresh-token'),
      buildInit({ method: 'POST' }, current?.token),
    )
    if (!res.ok) return false
    const parsed = authResponseSchema.safeParse(await res.json())
    if (!parsed.success) return false
    setSession({
      user: parsed.data.user,
      ...(isNative() ? { token: parsed.data.token, expMs: parsed.data.exp * 1000 } : {}),
    })
    return true
  } catch {
    return false
  }
}

/**
 * The one fetch the client uses. Injects the Authorization header (native) or
 * the cookie (web), and on a 401 refreshes the session and retries **once** —
 * a second 401 means the session is really gone, so the caller (route guard)
 * sends the user to the login screen.
 *
 * Returns the raw `Response`; parsing is the caller's decision.
 */
export async function apiFetchAuth(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(apiUrl(path), buildInit(init, getSession()?.token))
  if (res.status !== 401) return res

  const refreshed = await refreshSession()
  if (!refreshed) {
    clearSession()
    return res
  }
  return fetch(apiUrl(path), buildInit(init, getSession()?.token))
}

// ─── Session endpoints ───────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<Session> {
  const res = await fetch(
    apiUrl('/api/users/login'),
    buildInit({ method: 'POST', body: JSON.stringify({ email, password }) }, undefined),
  )
  if (!res.ok) throw new Error(`Login failed (${res.status})`)
  const { user, token, exp } = authResponseSchema.parse(await res.json())
  const session: Session = {
    user,
    ...(isNative() ? { token, expMs: exp * 1000 } : {}),
  }
  setSession(session)
  return session
}

export async function logout(): Promise<void> {
  try {
    await apiFetchAuth('/api/users/logout', { method: 'POST' })
  } finally {
    clearSession()
  }
}

/** Resolve the logged-in user from the server. `null` = not logged in. */
export async function fetchMe(): Promise<SessionUser | null> {
  const res = await apiFetchAuth('/api/users/me')
  if (!res.ok) return null
  const { user } = meResponseSchema.parse(await res.json())
  if (user) setSession({ ...(getSession() ?? {}), user })
  else clearSession()
  return user
}
